import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DollarSign, Eye, CheckCircle, Clock, Calendar } from 'lucide-react';
import { agentApi } from '@/services/agentApi';
import { AgentCommission } from '@/types/user';

export const CommissionsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [commissions, setCommissions] = useState<AgentCommission[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadCommissions();
  }, [currentPage, statusFilter, startDate, endDate]);

  const loadCommissions = async () => {
    setLoading(true);
    try {
      const result = await agentApi.getCommissions({
        page: currentPage,
        limit: 10,
        status: statusFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      setCommissions(result.commissions);
      setTotal(result.total || 0);
      // Calculate total pages from total and limit
      const limit = 10;
      setTotalPages(Math.ceil((result.total || 0) / limit));
    } catch (error) {
      console.error('Error loading commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'PAID') {
      return (
        <span className="badge bg-success d-flex align-items-center gap-1">
          <CheckCircle size={14} />
          Paid
        </span>
      );
    }
    return (
      <span className="badge bg-warning d-flex align-items-center gap-1">
        <Clock size={14} />
        Pending
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
    loadCommissions();
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h1 className="h3 fw-bold text-dark mb-1">Commissions</h1>
              <p className="text-muted mb-0">View and manage your commission earnings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label small text-muted">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  handleFilterChange();
                }}
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="PAID">Paid</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  handleFilterChange();
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted">End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  handleFilterChange();
                }}
              />
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setStatusFilter('');
                  setStartDate('');
                  setEndDate('');
                  setCurrentPage(1);
                  loadCommissions();
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Commissions Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : commissions.length === 0 ? (
            <div className="text-center py-5">
              <DollarSign size={48} className="text-muted mb-3" />
              <h5 className="text-muted">No commissions found</h5>
              <p className="text-muted">You don't have any commissions matching your filters.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Application</th>
                      <th>Student</th>
                      <th>University</th>
                      <th>Commission Rate</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((commission) => (
                      <motion.tr
                        key={commission.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="cursor-pointer"
                        onClick={() => navigate(`/commissions/${commission.id}`)}
                      >
                        <td>
                          <div className="fw-semibold small">
                            {commission.reference || `#${commission.id}`}
                          </div>
                        </td>
                        <td>
                          <div className="fw-semibold">
                            {commission.agent_student_applications?.reference || `#${commission.applicationId}`}
                          </div>
                        </td>
                        <td>
                          <div>
                            {commission.agent_student_applications?.firstName}{' '}
                            {commission.agent_student_applications?.lastName}
                          </div>
                          <small className="text-muted">
                            {commission.agent_student_applications?.email}
                          </small>
                        </td>
                        <td>
                          {commission.agent_student_applications?.universities ? (
                            <div>
                              <div className="fw-semibold">
                                {commission.agent_student_applications.universities.name}
                              </div>
                              <small className="text-muted">
                                {commission.agent_student_applications.universities.city},{' '}
                                {commission.agent_student_applications.universities.country}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-info">
                            {(commission.rate * 100).toFixed(2)}%
                          </span>
                        </td>
                        <td>
                          <span className="fw-bold text-success">
                            {formatCurrency(commission.amount)}
                          </span>
                        </td>
                        <td>{getStatusBadge(commission.status)}</td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <Calendar size={14} className="text-muted" />
                            <small>{formatDate(commission.createdAt)}</small>
                          </div>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/commissions/${commission.id}`);
                            }}
                          >
                            <Eye size={16} className="me-1" />
                            View
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <div className="text-muted">
                    Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, total)} of {total} commissions
                  </div>
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((page) => {
                          // Show first page, last page, current page, and pages around current
                          return (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          );
                        })
                        .map((page, index, array) => {
                          // Add ellipsis if there's a gap
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && (
                                <li className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              )}
                              <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            </React.Fragment>
                          );
                        })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

